import { Box, Flex } from "@chakra-ui/react";
import { useContext, useMemo } from "react";
import { useIntl } from "react-intl";

import { ProjectRead, User } from "@nens/rana/api-v1";
import { RanaSelectOptionLabel, TableFilterDropdownProps, TenantContext } from "@nens/ranalib";

import { Profile } from "@/lib/profile";
import { getCompleteName } from "@/utils/get-complete-name";

type UseContributorDropdownProps = {
  projects: ProjectRead[];
  selectedUserId: string | null;
  setSelectedUserId: (value: string | null) => void;
  setPage: (page: number) => void;
};

export const useContributorDropdown = ({
  projects,
  selectedUserId,
  setPage,
  setSelectedUserId,
}: UseContributorDropdownProps): TableFilterDropdownProps<string | null> => {
  const intl = useIntl();
  const authInfo = useContext(TenantContext);
  const user = !authInfo.isPublic ? authInfo.user : undefined;

  const contributors = useMemo(
    () =>
      projects
        .flatMap(({ contributors }) => contributors)
        .filter((value, index, self) => index === self.findIndex((t) => t.id === value.id))
        .sort((a, b) => {
          // Put user self first
          if (user) {
            if (a.id === user.id) return -1;
            if (b.id === user.id) return 1;
          }
          return a.givenName.localeCompare(b.givenName) || a.familyName.localeCompare(b.familyName);
        }),
    [projects, user]
  );

  // If the selected contributor is not in the list of contributors (e.g. because the project has been deleted),
  // reset the selected contributor to "all"
  if (
    selectedUserId &&
    contributors.length > 0 &&
    !contributors.find((contributor) => contributor.id === selectedUserId)
  ) {
    setSelectedUserId(null);
  }

  const getContributorDropdownOptions = (contributor: User) => {
    const completeName = getCompleteName(contributor);
    const youSuffix =
      user?.id === contributor.id
        ? ` (${intl.formatMessage({
            id: "projects.filter_by_contributor.you",
            defaultMessage: "You",
          })})`
        : "";

    return {
      value: contributor.id,
      searchBy: completeName,
      label: (searchString: string) => {
        return (
          <Flex alignItems="center" gap="2" whiteSpace="nowrap">
            <Profile noModal inTable user={contributor} />
            <Box>
              <RanaSelectOptionLabel searchString={searchString} content={completeName} />
              {youSuffix}
            </Box>
          </Flex>
        );
      },
    };
  };

  const contributorDropdown: TableFilterDropdownProps<string | null> = {
    selected: selectedUserId,
    setSelected: (id: string | null) => {
      setPage(1);
      setSelectedUserId(id);
    },
    label: intl.formatMessage({
      id: "projects.filter_by_contributor",
      defaultMessage: "Filter by contributor",
    }),
    options: [
      {
        value: null,
        label: intl.formatMessage({
          id: "projects.filter_by_contributor.all",
          defaultMessage: "All contributors",
        }),
      },
      ...contributors.map(getContributorDropdownOptions),
    ],
  };

  return contributorDropdown;
};
